/**
 * When a session's media is in the gallery — the pure half of the BK upload.
 *
 * Kept apart from the store and the pipeline so it can be tested on its own:
 * `mediaUpload.ts` wires it to both.
 */

/** The slice of a captured item the photo upload reads. */
export interface BkShot {
  id?: string
  photoUrl?: string
  videoUrl?: string
  gifUrl?: string
  filteredVersions?: Record<string, string>
}

/** The strip rides in capturedItems too; it is uploaded on its own. */
const isPhotograph = (item: BkShot): boolean =>
  !item.id?.includes('dreambooth-strip') && !item.videoUrl && !item.gifUrl

/**
 * Each photograph the way the strip printed it: in the chosen filter when its
 * bake has landed, the raw shot otherwise.
 */
export function photoUrlsFor(items: BkShot[], filterId: string | null | undefined): string[] {
  return items
    .filter(isPhotograph)
    .map((item) => (filterId && item.filteredVersions?.[filterId]) || item.photoUrl)
    .filter((url): url is string => Boolean(url && url.trim()))
}

/** True when every photograph has its bake for `filterId` (always, with no filter). */
export function bakesComplete(items: BkShot[], filterId: string | null | undefined): boolean {
  if (!filterId) return true
  return items.filter(isPhotograph).every((item) => Boolean(item.filteredVersions?.[filterId]))
}

/**
 * Resolve true once `done()` holds, or false after `timeoutMs` — never later.
 * Polled rather than subscribed: the bake writes the store from a queue that
 * knows nothing about who is waiting.
 */
export function waitUntil(done: () => boolean, timeoutMs: number, pollMs = 200): Promise<boolean> {
  return new Promise((resolve) => {
    if (done()) return resolve(true)
    const started = Date.now()
    const timer = setInterval(() => {
      if (done()) {
        clearInterval(timer)
        resolve(true)
      } else if (Date.now() - started >= timeoutMs) {
        clearInterval(timer)
        resolve(false)
      }
    }, pollMs)
  })
}

/**
 * One upload per session, however many pages ask for it.
 *
 * Capture starts the photo upload and Select asks again before printing (for a
 * session that reached Select without passing Capture's confirm); the second
 * ask gets the first upload back instead of sending every photo twice.
 */
export class OncePerSession {
  private readonly started = new Map<string, Promise<void>>()

  run(sessionId: string, start: () => Promise<void>): Promise<void> {
    const existing = this.started.get(sessionId)
    if (existing) return existing
    const upload = start()
    this.started.set(sessionId, upload)
    return upload
  }

  get(sessionId: string): Promise<void> | undefined {
    return this.started.get(sessionId)
  }

  forget(sessionId: string): void {
    this.started.delete(sessionId)
  }
}

/**
 * Wait for uploads to settle, succeeded or not, but never past `capMs`.
 * Reports how long that took and whether the cap cut it short, for the log.
 */
export async function settleWithin(
  uploads: Promise<unknown>[],
  capMs: number
): Promise<{ waitedMs: number; capped: boolean }> {
  const started = Date.now()
  let timer: ReturnType<typeof setTimeout> | undefined
  const capped = await Promise.race([
    Promise.allSettled(uploads).then(() => false),
    new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(true), capMs)
    })
  ])
  clearTimeout(timer)
  return { waitedMs: Date.now() - started, capped }
}
